import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConfirmDialog from '../ConfirmDialog';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';

function ManagePayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  // Confirm dialog state
  const [confirmState, setConfirmState] = useState({ open: false, paymentId: null, verified: false });

  // Notes prompt dialog state
  const [notesState, setNotesState] = useState({ open: false, paymentId: null, verified: false, value: '' });

  const { toasts, addToast, removeToast } = useToast();

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/payments', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setPayments(response.data.payments);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar comprovantes');
        console.error('Error fetching payments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const handleVerifyClick = (paymentId, verified) => {
    setConfirmState({ open: true, paymentId, verified });
  };

  const handleConfirmVerify = () => {
    setConfirmState({ open: false, paymentId: null, verified: false });
    // Open notes prompt
    setNotesState({
      open: true,
      paymentId: confirmState.paymentId,
      verified: confirmState.verified,
      value: ''
    });
  };

  const handleCancelConfirm = () => {
    setConfirmState({ open: false, paymentId: null, verified: false });
  };

  const handleCancelNotes = () => {
    setNotesState({ open: false, paymentId: null, verified: false, value: '' });
  };

  const handleSubmitNotes = async () => {
    const { paymentId, verified, value } = notesState;
    setNotesState({ open: false, paymentId: null, verified: false, value: '' });
    setActionLoading((prev) => ({ ...prev, [paymentId]: true }));

    try {
      const res = await axios.post(`/api/payments/${paymentId}/verify`, {
        verified,
        verification_notes: value || null
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setPayments(payments.map(p =>
        p.id === paymentId
          ? {
              ...p,
              verified,
              verification_notes: value,
              id: res.data.payment.id,
              comprovante_url: res.data.payment.comprovante_url
            }
          : p
      ));

      addToast(verified ? 'Pagamento verificado com sucesso!' : 'Pagamento recusado!', verified ? 'success' : 'warning');
    } catch (err) {
      addToast('Erro ao atualizar verificação', 'error');
      console.error('Error updating payment verification:', err);
    } finally {
      setActionLoading((prev) => ({ ...prev, [paymentId]: false }));
    }
  };

  if (loading) {
    return <div className="p-6">Carregando comprovantes...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-2xl font-bold mb-6 text-primary">Verificar Pagamentos</h1>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full min-w-[500px] divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Usuário</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Comprovante</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {payments.map((payment) => (
              <tr key={payment.id} className="hover:bg-gray-50">
                <td className="px-3 lg:px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 break-words">
                    {payment.user.name}
                  </div>
                  {payment.user.nickname && (
                    <div className="text-sm text-gray-500">@{payment.user.nickname}</div>
                  )}
                </td>
                <td className="px-3 lg:px-6 py-4">
                  {payment.comprovante_url && payment.comprovante_url !== 'Aprovação Manual (Sem anexo)' ? (
                    <a
                      href={payment.comprovante_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-tertiary hover:text-blue-900 break-all focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                    >
                      Ver Comprovante
                    </a>
                  ) : (
                    <span className="text-gray-500">Sem anexo</span>
                  )}
                </td>
                <td className="px-3 lg:px-6 py-4">
                  {payment.verified ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-primary">
                      Verificado
                    </span>
                  ) : payment.verification_notes ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      Recusado
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Pendente
                    </span>
                  )}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm font-medium">
                  <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
                    <button
                      onClick={() => handleVerifyClick(payment.id, true)}
                      disabled={actionLoading[payment.id]}
                      className="bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1.5 rounded-md min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
                    >
                      Verificar
                    </button>
                    <button
                      onClick={() => handleVerifyClick(payment.id, false)}
                      disabled={actionLoading[payment.id]}
                      className="bg-red-50 hover:bg-red-100 text-red-700 px-3 py-1.5 rounded-md min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
                    >
                      Recusar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirm Dialog - first step */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.verified ? 'Verificar Pagamento' : 'Recusar Pagamento'}
        message={confirmState.verified ? 'Deseja verificar este pagamento?' : 'Deseja recusar este pagamento?'}
        confirmLabel={confirmState.verified ? 'Verificar' : 'Recusar'}
        cancelLabel="Cancelar"
        variant={confirmState.verified ? 'primary' : 'danger'}
        onConfirm={handleConfirmVerify}
        onCancel={handleCancelConfirm}
      />

      {/* Notes Prompt Dialog - second step */}
      {notesState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-sm p-4 mx-auto">
            <div className="bg-white rounded-xl shadow-xl">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {notesState.verified ? 'Observações (opcional)' : 'Motivo da recusa'}
                </h3>
                <textarea
                  value={notesState.value}
                  onChange={(e) => setNotesState({ ...notesState, value: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[80px] resize-y"
                  placeholder={notesState.verified ? 'Observações (opcional)...' : 'Informe o motivo da recusa...'}
                  autoFocus
                />
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCancelNotes}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSubmitNotes}
                    className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg focus:ring-4 transition-colors ${notesState.verified ? 'bg-primary hover:bg-primary-dark focus:ring-green-300' : 'bg-red-600 hover:bg-red-700 focus:ring-red-300'}`}
                  >
                    {notesState.verified ? 'Confirmar Verificação' : 'Confirmar Recusa'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default ManagePayments;
